import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// Helpers
import groupBy from 'lodash/groupBy'
import { resolveErrorMessage } from 'helpers'

// Api
import {
  getUploadedOR,
  paymentORDownload,
  paymentORRemove,
  paymentORUpload,
} from 'api/payment'

// Models
import PaymentBreakdownModel from 'models/PaymentBreakdown'

// Use
import useMounted from 'use/mounted'
import { useUploadHook } from 'use/upload'

// Services
import message from 'services/message'

// Components
import Row from 'ui/Row'
import Col from 'ui/Col'
import Loading from 'ui/Loading'
import Text from 'ui/Text'
import Button from 'ui/Button'
import FileHelp from 'ui/FileHelp'
import FileDropZone from 'ui/FileDropZone'
import FileItem from 'ui/FileItem'

const accept = '.jpg,.jpeg,.png,.pdf'
const multiple = true
const maxFileSize = 3

function PaymentOR({ payment }) {
  const isMounted = useMounted()

  // State
  const [fetching, setFetching] = useState(false)
  const [listOR, setListOR] = useState([])

  // Get uploaded or
  useEffect(() => {
    if (payment && payment.id) {
      setFetching(true)
      getUploadedOR(payment.id)
        .then((data) => {
          if (isMounted.current) {
            setListOR(data)
          }
        })
        .finally(() => {
          if (isMounted.current) {
            setFetching(false)
          }
        })
    }
  }, [isMounted, payment])

  // On upload callback
  const onUpload = async ({ file, onUploadProgress, onError, onSuccess }) => {
    try {
      const data = await paymentORUpload({
        file,
        paymentId: payment.id,
        onUploadProgress,
      })
      onSuccess(data)
    } catch (error) {
      onError(error)
    }
  }

  // Use upload hook
  const {
    files,
    count: { pending },
    uploading,
    upload,
    removeFile,
    onFileChange,
  } = useUploadHook({
    accept,
    maxFileSize,
    multiple,
    allowDuplicate: false,
    uploadedFiles: listOR,
    onUpload,
  })

  const uploadedFiles = useMemo(() => {
    const grouped = groupBy(
      files.filter((x) => x.status === 'done'),
      (x) => x.uploadedAt
    )
    return Object.keys(grouped).map((date) => {
      return {
        files: grouped[date],
        date,
      }
    })
  }, [files])

  const uploadingFiles = useMemo(
    () => files.filter((file) => file.status !== 'done'),
    [files]
  )

  // On download or
  const onDownloadOR = async (file, setLoading) => {
    try {
      setLoading(true)

      await paymentORDownload({
        paymentId: payment.id,
        fileName: file.name,
      })
    } catch (error) {
      message.error(await resolveErrorMessage(error))
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  // On remove or
  const onRemoveOR = async (file, setLoading) => {
    try {
      setLoading(true)

      await paymentORRemove({
        paymentId: payment.id,
        fileName: file.name,
      })

      removeFile(file.id)
    } catch (error) {
      message.error(await resolveErrorMessage(error))
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  return (
    <>
      <Text size={18} paddingBottom={20} paddingTop={0} bold color="#000000">
        Official receipt
      </Text>

      <FileHelp
        title="Uploading of official receipt below is mandatory. You may come back and upload later."
        items={[
          '- File must be less than 3MB',
          '- File type must be .jpg, .png or .pdf',
        ]}
      />

      <Row>
        <Col xs={12} sm={12} md={7} lg={7} xl={7}>
          <div style={{ paddingBottom: '20px' }}>
            <FileDropZone
              size="sm"
              accept={accept}
              multiple={multiple}
              onChange={onFileChange}
            />

            {uploadingFiles.map((file, index) => {
              return (
                <FileItem
                  key={index}
                  name={file.name}
                  error={file.error}
                  status={file.status}
                  percent={file.percent}
                  onRemove={() => removeFile(file.id)}
                />
              )
            })}

            <Button
              style={{ padding: '0 70px', marginTop: '10px' }}
              loading={uploading}
              disabled={pending === 0}
              onClick={upload}
            >
              Upload
            </Button>
          </div>

          <Loading spinning={fetching}>
            {uploadedFiles.length > 0 && (
              <div className="title" style={{ paddingBottom: '5px' }}>
                Attachment/s:
              </div>
            )}

            {uploadedFiles.map((item, index1) => {
              return (
                <div
                  style={{ paddingBottom: '15px', fontSize: 18 }}
                  key={index1}
                >
                  <Text
                    size={16}
                    color="#6F8191"
                    paddingBottom={10}
                    paddingTop={0}
                  >
                    Uploaded: {item.date}
                  </Text>

                  {item.files.map((file, index2) => {
                    return (
                      <FileItem
                        key={index2}
                        name={file.name}
                        status={file.status}
                        onDownload={(e, setLoading) =>
                          onDownloadOR(file, setLoading)
                        }
                        onRemove={(e, setLoading) =>
                          onRemoveOR(file, setLoading)
                        }
                      />
                    )
                  })}
                </div>
              )
            })}
          </Loading>
        </Col>
      </Row>
    </>
  )
}

PaymentOR.propTypes = {
  payment: PropTypes.instanceOf(PaymentBreakdownModel),
}

PaymentOR.defaultProps = {
  payment: new PaymentBreakdownModel(),
}

export default PaymentOR
